import { useEffect, useState } from "react";
import { ProjectTree } from "./components/ProjectTree";
import { MessageViewer } from "./components/MessageViewer";
import { TokenStatsViewer } from "./components/TokenStatsViewer";
import { AnalyticsDashboard } from "./components/AnalyticsDashboard";
import { LoginPage } from "./components/LoginPage";
import { useAppStore } from "./store/useAppStore";
import { useAuthStore } from "./store/useAuthStore";
import { useAnalytics } from "./hooks/useAnalytics";
import { healthCheck } from "./api/httpClient";

import { useTranslation } from "react-i18next";
import { AppErrorType } from "@anthropic/shared";
import type { ClaudeSession, ClaudeProject } from "@anthropic/shared";
import { AlertTriangle, Loader2, MessageSquare } from "lucide-react";
import { useLanguageStore } from "./store/useLanguageStore";
import { type SupportedLanguage } from "./i18n";

import "./App.css";
import { cn } from "./utils/cn";
import { COLORS } from "./constants/colors";
import { Header } from "@/layouts/Header/Header";
import { ModalContainer } from "./layouts/Header/SettingDropdown/ModalContainer";

function App() {
  const {
    projects,
    sessions,
    selectedProject,
    selectedSession,
    messages,
    pagination,
    isLoading,
    isLoadingProjects,
    isLoadingSessions,
    isLoadingMessages,
    error,
    sessionTokenStats,
    projectTokenStats,
    selectProject,
    selectSession,
    loadMoreMessages,
  } = useAppStore();

  const { isAuthenticated, isCheckingAuth } = useAuthStore();

  const {
    state: analyticsState,
    actions: analyticsActions,
    computed,
  } = useAnalytics();

  const { t, i18n: i18nInstance } = useTranslation("common");
  const { t: tComponents } = useTranslation("components");
  const { t: tMessages } = useTranslation("messages");
  const { language } = useLanguageStore();

  // Auth state
  const [authEnabled, setAuthEnabled] = useState<boolean | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Keep current view when selecting session
  const handleSessionSelect = async (session: ClaudeSession) => {
    await selectSession(session);
  };

  // Check auth and initialize app - only run once on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        // First check if auth is enabled via health check
        const health = await healthCheck();
        setAuthEnabled(health.authEnabled);

        // If auth is enabled, verify token
        if (health.authEnabled) {
          const isValid = await useAuthStore.getState().verifyToken();
          if (!isValid) {
            // Not authenticated, will show login page
            setIsInitializing(false);
            return;
          }
        } else {
          // Auth not enabled, clear the checking state
          useAuthStore.setState({ isCheckingAuth: false });
        }

        // Load language and initialize app
        await useLanguageStore.getState().loadLanguage().catch(() => {
          // Ignore language load error
        });
        await useAppStore.getState().initializeApp();
      } catch (err) {
        console.error("Failed to initialize:", err);
        // If health check fails, assume auth is not enabled
        setAuthEnabled(false);
        useAuthStore.setState({ isCheckingAuth: false });
        await useLanguageStore.getState().loadLanguage().catch(() => {});
        await useAppStore.getState().initializeApp();
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const currentLang = lng.startsWith("zh")
        ? lng.includes("TW") || lng.includes("HK")
          ? "zh-TW"
          : "zh-CN"
        : lng.split("-")[0];

      if (
        currentLang &&
        currentLang !== language &&
        ["en", "ko", "ja", "zh-CN", "zh-TW"].includes(currentLang)
      ) {
        useLanguageStore.setState({
          language: currentLang as SupportedLanguage,
        });
      }
    };

    i18nInstance.on("languageChanged", handleLanguageChange);
    return () => {
      i18nInstance.off("languageChanged", handleLanguageChange);
    };
  }, [language, i18nInstance]);

  // Project select handler (includes analytics state reset)
  const handleProjectSelect = async (project: ClaudeProject) => {
    const wasAnalyticsOpen = computed.isAnalyticsView;
    const wasTokenStatsOpen = computed.isTokenStatsView;

    // Reset existing analytics data
    if (!computed.isMessagesView) {
      analyticsActions.switchToMessages();
    }

    // Select project
    await selectProject(project);

    // If analytics tab was open, auto-load analytics for new project
    if (wasAnalyticsOpen) {
      try {
        await analyticsActions.switchToAnalytics();
      } catch (error) {
        console.error("Failed to auto-load analytics for new project:", error);
      }
    }

    // If token stats tab was open, auto-load token stats for new project
    if (wasTokenStatsOpen) {
      try {
        await analyticsActions.switchToTokenStats();
      } catch (error) {
        console.error(
          "Failed to auto-load token stats for new project:",
          error
        );
      }
    }
  };

  // Show loading spinner while initializing
  if (isInitializing || isCheckingAuth) {
    return (
      <div
        className={cn(
          "h-screen flex items-center justify-center",
          COLORS.ui.background.primary
        )}
      >
        <div className="text-center">
          <Loader2
            className={cn("w-12 h-12 mx-auto animate-spin", COLORS.ui.text.muted)}
          />
          <p className={cn("mt-4", COLORS.ui.text.muted)}>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if auth is enabled and not authenticated
  if (authEnabled && !isAuthenticated) {
    return <LoginPage />;
  }

  if (error && error.type !== AppErrorType.CLAUDE_FOLDER_NOT_FOUND) {
    return (
      <div
        className={cn(
          "h-screen flex items-center justify-center",
          COLORS.semantic.error.bg
        )}
      >
        <div className="text-center">
          <div className="mb-4">
            <AlertTriangle
              className={cn("w-12 h-12 mx-auto", COLORS.semantic.error.icon)}
            />
          </div>
          <h1
            className={cn(
              "text-xl font-semibold mb-2",
              COLORS.semantic.error.text
            )}
          >
            {t("errorOccurred")}
          </h1>
          <p className={cn("mb-4", COLORS.semantic.error.text)}>
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className={cn(
              "px-4 py-2 rounded-lg transition-colors",
              COLORS.semantic.error.bg,
              COLORS.semantic.error.text
            )}
          >
            {t("retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn("h-screen flex flex-col", COLORS.ui.background.primary)}
      >
        {/* Header */}
        <Header />

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar */}
          <ProjectTree
            projects={projects}
            sessions={sessions}
            selectedProject={selectedProject}
            selectedSession={selectedSession}
            onProjectSelect={handleProjectSelect}
            onSessionSelect={handleSessionSelect}
            isLoading={isLoadingProjects || isLoadingSessions}
          />

          {/* Main Content Area */}
          <div className="w-full flex flex-col relative">
            {/* Content Header */}
            {(selectedSession ||
              computed.isTokenStatsView ||
              computed.isAnalyticsView) && (
              <div
                className={cn(
                  "p-4 border-b",
                  COLORS.ui.background.secondary,
                  COLORS.ui.border.light
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h2
                      className={cn(
                        "text-lg font-semibold",
                        COLORS.ui.text.primary
                      )}
                    >
                      {computed.isAnalyticsView
                        ? tComponents("analytics.dashboard")
                        : computed.isTokenStatsView
                        ? tMessages("tokenStats.title")
                        : tComponents("message.conversation")}
                    </h2>
                    <span className={cn("text-sm", COLORS.ui.text.secondary)}>
                      {selectedSession?.summary ||
                        tComponents("session.summaryNotFound")}
                    </span>
                    {computed.isMessagesView && selectedSession && (
                      <div>
                        <p className={cn("text-sm mt-1", COLORS.ui.text.muted)}>
                          {pagination.totalCount >= messages.length &&
                            ` ${pagination.totalCount || "-"}개 • `}
                          {selectedSession.has_tool_use
                            ? tComponents("tools.toolUsed")
                            : tComponents("tools.generalConversation")}
                          {selectedSession.has_errors &&
                            ` • ${tComponents("tools.errorOccurred")}`}
                        </p>
                      </div>
                    )}
                    {computed.isTokenStatsView && (
                      <p className={cn("text-sm mt-1", COLORS.ui.text.muted)}>
                        {tComponents("analytics.tokenUsageDetailed")}
                      </p>
                    )}
                    {computed.isAnalyticsView && (
                      <p className={cn("text-sm mt-1", COLORS.ui.text.muted)}>
                        {selectedSession
                          ? tComponents("analytics.projectSessionAnalysis")
                          : tComponents("analytics.projectOverallAnalysis")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {computed.isAnalyticsView ? (
                <div className="h-full overflow-y-auto">
                  <AnalyticsDashboard />
                </div>
              ) : computed.isTokenStatsView ? (
                <div className="h-full overflow-y-auto p-6 space-y-8">
                  <TokenStatsViewer
                    title={tMessages("tokenStats.title")}
                    sessionStats={sessionTokenStats}
                    projectStats={projectTokenStats}
                  />
                </div>
              ) : selectedSession ? (
                <MessageViewer
                  messages={messages}
                  pagination={pagination}
                  isLoading={isLoading}
                  selectedSession={selectedSession}
                  onLoadMore={loadMoreMessages}
                />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className={cn("text-center", COLORS.ui.text.muted)}>
                    <MessageSquare
                      className={cn(
                        "w-16 h-16 mx-auto mb-4",
                        COLORS.ui.text.disabledDark
                      )}
                    />
                    <p className="text-lg mb-2">
                      {tComponents("session.select")}
                    </p>
                    <p className="text-sm">
                      {tComponents("session.selectDescription")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status Bar */}
        <div
          className={cn(
            "px-6 py-2 border-t",
            COLORS.ui.background.secondary,
            COLORS.ui.border.light
          )}
        >
          <div
            className={cn(
              "flex items-center justify-between text-xs",
              COLORS.ui.text.muted
            )}
          >
            <div className="flex items-center space-x-4">
              <span>
                {tComponents("project.count", { count: projects.length })}
              </span>
              <span>
                {tComponents("session.count", { count: sessions.length })}
              </span>
              {selectedSession && computed.isMessagesView && (
                <span>
                  {tComponents("message.countWithTotal", {
                    current: messages.length,
                    total: pagination.totalCount || messages.length,
                  })}
                </span>
              )}
              {computed.isTokenStatsView && sessionTokenStats && (
                <span>
                  {tComponents("analytics.currentSessionTokens", {
                    count: sessionTokenStats.total_tokens,
                  })}
                </span>
              )}
              {computed.isAnalyticsView && analyticsState.projectSummary && (
                <span>
                  {tComponents("analytics.projectTokens", {
                    count: analyticsState.projectSummary.total_tokens,
                  })}
                </span>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {(isLoading ||
                isLoadingProjects ||
                isLoadingSessions ||
                isLoadingMessages ||
                computed.isAnyLoading) && (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>
                    {computed.isAnyLoading &&
                      tComponents("status.loadingStats")}
                    {isLoadingProjects && tComponents("status.scanning")}
                    {isLoadingSessions && tComponents("status.loadingSessions")}
                    {isLoadingMessages && tComponents("status.loadingMessages")}
                    {isLoading && tComponents("status.initializing")}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Note: SimpleUpdateManager removed for web version - updates are handled server-side */}
      </div>

      {/* Modals */}
      <ModalContainer />
    </>
  );
}

export default App;
